import { StyledBusCard } from '../Layout/styles';
import { CardContent, FormControl, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import UndoIcon from '@mui/icons-material/Undo';
import ClearIcon from '@mui/icons-material/Clear';
import { Bus } from '../../@types/contractors';
import { useState } from 'react';
import { useProvideSnackBar } from '../../hooks';
import { usePreconfiguredFetch } from '../../hooks/useProvidePreconfiguredFetch';

interface BusCardProp {
    bus: Bus;
    onDelete: (buszId: number) => void;
    onUpdate: (bus: Bus) => void;
}

const BusCard = ({ bus, onUpdate, onDelete }: BusCardProp) => {
    const { fetchResource } = usePreconfiguredFetch();
    const { showError, showSuccess, showResponseError } = useProvideSnackBar();

    const [inEdit, setInEdit] = useState<boolean>(!bus.Manufacturer || !bus.Type || !bus.Capacity || !bus.YearOfManufacture);

    const [manufacturer, setManufacturer] = useState<string>(bus.Manufacturer);
    const [type, setType] = useState<string>(bus.Type);
    const [yearOfManufacture, setYearOfManufacture] = useState<string>(bus.YearOfManufacture);
    const [capacity, setCapacity] = useState<number>(bus.Capacity);
    const [category, setCategory] = useState<string>(bus.Category);
    const handleSave = () => {
        const updatedBus = {
            ID: bus.ID,
            Manufacturer: manufacturer,
            Type: type,
            YearOfManufacture: yearOfManufacture,
            Capacity: capacity,
            Category: category,
        };

        if (JSON.stringify(updatedBus) === JSON.stringify(bus)) {
            setInEdit(false);
            return;
        }
        fetchUpdateBus(updatedBus);
        onUpdate(updatedBus);
    };

    const fetchUpdateBus = async (bus: Bus) => {
        try {
            const response = await fetchResource(`/api/v1/bus/${bus.ID}`, {
                method: 'PUT',
                body: JSON.stringify(bus),
            });

            if (response.ok) {
                showSuccess('Sikeres frissítés!');
                setInEdit(false);
            } else {
                showResponseError(response);
            }
        } catch (error: any) {
            showError(`Hiba történt a busz frissítése közben`);
        }
    };

    const handleDeleteBus = async () => {
        try {
            const response = await fetchResource(`/api/v1/bus/${bus.ID}`, {
                method: 'DELETE',
            });
            if (response.ok) {
                showSuccess('Sikeres törlés!');
            } else {
                showResponseError(response);
            }
        } catch (error: any) {
            showError(`Hiba történt a busz törlése közben`);
        }
    };

    const handleResetData = () => {
        setManufacturer(bus.Manufacturer);
        setType(bus.Type);
        setCapacity(bus.Capacity);
        setYearOfManufacture(bus.YearOfManufacture);
        setCategory(bus.Category);
    };

    return (
        <Grid xs={12}>
            <StyledBusCard variant={'elevation'}>
                <CardContent sx={{ m: 0, pb: '0!important', pt: 1, px: 0.5 }}>
                    <Grid container flexWrap={'wrap'} m={0} p={0} spacing={1}>
                        <Grid md={2}>
                            <TextField
                                label='Gyártó'
                                variant='standard'
                                value={manufacturer}
                                onChange={e => setManufacturer(e.target.value)}
                                size='small'
                                disabled={!inEdit}
                            />
                        </Grid>
                        <Grid md={2}>
                            <TextField label='Típus' variant='standard' value={type} onChange={e => setType(e.target.value)} size='small' disabled={!inEdit} />
                        </Grid>
                        <Grid md={2}>
                            <TextField
                                label='Gyártási év'
                                variant='standard'
                                value={yearOfManufacture}
                                onChange={e => setYearOfManufacture(e.target.value)}
                                size='small'
                                fullWidth
                                disabled={!inEdit}
                            />
                        </Grid>
                        <Grid md={2}>
                            <TextField
                                label='Fő'
                                variant='standard'
                                value={capacity}
                                onChange={e => {
                                    const capacity = parseInt(e.target.value);
                                    if (!isNaN(capacity)) {
                                        setCapacity(capacity);
                                    }
                                }}
                                inputProps={{ type: 'number', min: '0' }}
                                size='small'
                                disabled={!inEdit}
                            />
                        </Grid>
                        <Grid md={2}>
                            <FormControl variant='standard' size='small' fullWidth disabled={!inEdit}>
                                <InputLabel>Kategória</InputLabel>
                                <Select value={category} onChange={e => setCategory(e.target.value)}>
                                    <MenuItem value='nagy'>Nagy (49 fő)</MenuItem>
                                    <MenuItem value='midi'>Midi (39 fő)</MenuItem>
                                    <MenuItem value='mini'>Mini (21 fő)</MenuItem>
                                    <MenuItem value='mikro'>Mikro (8 fő)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid md={1} justifyContent={'end'} alignItems={'center'} display={'flex'} xsOffset={1}>
                            <IconButton
                                title={'Busz szerkesztése / Mentése'}
                                size='small'
                                sx={{ padding: 0 }}
                                onClick={() => {
                                    if (!inEdit) {
                                        setInEdit(true);
                                        return;
                                    }
                                    handleSave();
                                }}>
                                {inEdit ? <SaveIcon color={'info'} /> : <EditIcon color={'info'} />}
                            </IconButton>
                            <IconButton
                                title={'Busz törlése / Változtatások visszavonása'}
                                size='small'
                                sx={{ padding: 0 }}
                                onClick={() => {
                                    setInEdit(false);
                                    if (inEdit) {
                                        handleResetData();
                                        return;
                                    }
                                    handleDeleteBus();
                                    onDelete(bus.ID);
                                }}>
                                {inEdit ? <UndoIcon color={'info'} /> : <ClearIcon color={'error'} />}
                            </IconButton>
                        </Grid>
                    </Grid>
                </CardContent>
            </StyledBusCard>
        </Grid>
    );
};

export { BusCard };
